import { Option } from "admin-ui/lib/form/types"
import { EStatus, OptionItem, PaginationMetadata } from "types/common"
import {
  EDiscountConditionPrice,
  EDiscountType,
  EPromotionCompareType,
} from "utils/api/client/types"

export const DEFAULT_LOGO_TEXT = "/logo-text.webp"
export const DEFAULT_LOGO = "/default-product-image.png"
export const FALLBACK_LOGO = "/logo.webp"

export const DEFAULT_PAGINATION_LIMIT = 10
export const PAGINATION = {
  limit: 15,
  page: 1,
}

export const FILE_IMAGE = {
  accepts: ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"],
  size: 1024 * 1024 * 5, // 5MB,
}

export const FILE_CSV = {
  accepts: [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    "application/vnd.ms-excel", // .xls
    ".xlsx",
    ".xls",
  ],
  size: 1024 * 1024 * 50, // 50MB,
}

export const FILE_VIDEO_IMAGE_PDF = {
  accepts: [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
    "image/gif",
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/ogv",
    "video/x-m4v",
    "application/pdf",
  ],
  size: 1024 * 1024 * 20, // 10MB,
}

export const FILE_PDF = {
  accepts: ["application/pdf"],
  size: 1024 * 1024 * 10, // 10MB,
}

export const FILE_VIDEO = {
  accepts: ["video/*"],
  size: 1024 * 1024 * 10, // 10MB,
}

export enum EErpStatus {
  ACTIVE = "ACTIVE",
  ACTIVE_SHORT_EXPIRY = "ACTIVE (SHORT-EXPIRY)",
  NEW = "NEW",
  SLOW_MOVING = "SLOW MOVING",
  SPECIAL_TO_ORDER = "SPECIAL-TO-ORDER",
  DISCONTINUED = "DISCONTINUED",
  DISCONTINUED_BY_PRINCIPAL = "DISCONTINUED BY PRINCIPAL",
  OUT_OF_STOCK_PRINCIPAL = "OUT OF STOCK - PRINCIPAL",
  INACTIVE = "INACTIVE",
  OUT_OF_STOCK = "OUT OF STOCK",
}

export enum EForensicGroup {
  MEDICAL_DEVICE = "MD",
  PRESCRIPTION_ONLY_MEDICINE = "POM",
  PERSONAL = "PERSONAL",
  GENERAL_SALE_LIST = "GSL",
  PHARMACY_ONLY_MEDICINE = "P",
  HEALTH_SUPPLEMENT = "HS",
  FOOD = "FOOD",
  CONTROLLED_DRUGS = "CD",
}

export enum EProductStatus {
  ACTIVE = "active",
  DISABLED = "disabled",
  HIDDEN = "hidden",
  DISAPPROVED = "disapproved",
}

export const ErpStatusOptions: OptionItem[] = [
  { label: "Active", value: EErpStatus.ACTIVE },
  { label: "Inactive", value: EErpStatus.INACTIVE },
  { label: "Discontinued", value: EErpStatus.DISCONTINUED },
  { label: "Slow moving", value: EErpStatus.SLOW_MOVING },
  { label: "New", value: EErpStatus.NEW },
  { label: "Special to order", value: EErpStatus.SPECIAL_TO_ORDER },
  { label: "Short expiry", value: EErpStatus.ACTIVE_SHORT_EXPIRY },
  {
    label: "Discontinued by principal",
    value: EErpStatus.DISCONTINUED_BY_PRINCIPAL,
  },
  { label: "Out of stock", value: EErpStatus.OUT_OF_STOCK },
  { label: "Out of stock principal", value: EErpStatus.OUT_OF_STOCK_PRINCIPAL },
]

export const ForensicGroupOptions: OptionItem[] = [
  { label: "MD", value: EForensicGroup.MEDICAL_DEVICE },
  { label: "POM", value: EForensicGroup.PRESCRIPTION_ONLY_MEDICINE },
  { label: "PERSONAL", value: EForensicGroup.PERSONAL },
  { label: "GSL", value: EForensicGroup.GENERAL_SALE_LIST },
  { label: "P", value: EForensicGroup.PHARMACY_ONLY_MEDICINE },
  { label: "HS", value: EForensicGroup.HEALTH_SUPPLEMENT },
  { label: "FOOD", value: EForensicGroup.FOOD },
  { label: "CD", value: EForensicGroup.CONTROLLED_DRUGS },
]

export const ProductStatusOptions: OptionItem[] = [
  { label: "Active", value: EProductStatus.ACTIVE },
  { label: "Disabled", value: EProductStatus.DISABLED },
  { label: "Hidden", value: EProductStatus.HIDDEN },
  { label: "Disapproved", value: EProductStatus.DISAPPROVED },
]

export const ProductUnitOptions: OptionItem[] = [
  { label: "Box", value: "box" },
  { label: "Bot", value: "bot" },
  { label: "TB", value: "tb" },
  { label: "Pkt", value: "pkt" },
  { label: "Rol", value: "rol" },
  { label: "Jar", value: "jar" },
  { label: "Pcs", value: "pcs" },
  { label: "Set", value: "set" },
]

export const DiscountConditionPriceOptions: OptionItem[] = [
  { label: "Equal", value: EDiscountConditionPrice.Equal },
  { label: "Not equal", value: EDiscountConditionPrice.NotEqual },
  { label: "Equal or less", value: EDiscountConditionPrice.EqualOrLess },
  { label: "Equal or greater", value: EDiscountConditionPrice.EqualOrGreater },
  { label: "Less than", value: EDiscountConditionPrice.LessThan },
  { label: "Greater than", value: EDiscountConditionPrice.GreaterThan },
]

export const DiscountTypeOptions: OptionItem[] = [
  {
    label: "Percentage",
    value: EDiscountType.Percentage,
  },
  { label: "Fixed", value: EDiscountType.Fixed },
]

export const DiscountTypeV2Options: OptionItem[] = [
  { label: "Absolute (Fixed Amount)", value: EDiscountType.Fixed },
  {
    label: "Percentage %",
    value: EDiscountType.Percentage,
  },
]

export const AmountTypeOptions: OptionItem[] = [
  {
    label: "Absolute (points)",
    value: EDiscountType.Percentage,
  },
  { label: "Absolute (S$)", value: EDiscountType.Fixed },
]

export const AmountTypeOptionsV2: OptionItem[] = [
  {
    label: "Percentage (%)",
    value: EDiscountType.Percentage,
  },
  { label: "Absolute (S$)", value: EDiscountType.Fixed },
]

export const YesNoOptions: OptionItem[] = [
  { label: "Yes", value: EStatus.Yes },
  { label: "No", value: EStatus.No },
]

export const ActiveDisabledOptions: OptionItem[] = [
  {
    label: "Active",
    value: EStatus.Active,
  },
  {
    label: "Disable",
    value: EStatus.Disabled,
  },
]

export const ActiveInactiveOptions: OptionItem[] = [
  {
    label: "Active",
    value: EStatus.Active,
  },
  {
    label: "Inactive",
    value: EStatus.Inactive,
  },
]

export const ActiveDisabledV2Options: OptionItem[] = [
  {
    label: "Active",
    value: EStatus.Active,
  },
  {
    label: "Disabled",
    value: EStatus.Inactive,
  },
]

export const DefaultMetadata: PaginationMetadata = {
  limit: PAGINATION.limit,
  page: PAGINATION.page,
  count: 0,
  offset: 0,
  totalPage: 0,
}

export const PRODUCT_TEMPLATE_CSV =
  "https://adminbackendstorage.blob.core.windows.net/static/product-template.csv"

export const PRICE_TEMPLATE_CSV =
  "https://adminbackendstorage.blob.core.windows.net/static/price-template.csv"

export enum ETierPromotionType {
  All = "all",
  Include = "include",
  Exclude = "exclude",
}
export const TierPromotionCustomerOptions: OptionItem[] = [
  {
    label: "All",
    value: ETierPromotionType.All,
  },
  {
    label: "Customer who can access",
    value: ETierPromotionType.Include,
  },
  {
    label: "Customer  who cannot access",
    value: ETierPromotionType.Exclude,
  },
]

export const PromotionCustomerGroupOptions: Option[] = [
  {
    label: "All",
    value: EPromotionCompareType.All,
  },
  {
    label: "Applicable to selected customer group",
    value: EPromotionCompareType.Include,
  },
  {
    label: "Not applicable to selected customer group",
    value: EPromotionCompareType.Exclude,
  },
]

export const PromotionCategoryOptions: Option[] = [
  {
    label: "All",
    value: EPromotionCompareType.All,
  },
  {
    label: "Applicable to selected category",
    value: EPromotionCompareType.Include,
  },
  {
    label: "Not applicable to selected category",
    value: EPromotionCompareType.Exclude,
  },
]

export const PromotionPrincipleOptions: Option[] = [
  {
    label: "All",
    value: EPromotionCompareType.All,
  },
  {
    label: "Applicable to selected products",
    value: EPromotionCompareType.Include,
  },
  {
    label: "Not applicable to selected products",
    value: EPromotionCompareType.Exclude,
  },
]

export const TierPromotionPrincipleOptions: Option[] = [
  {
    label: "All",
    value: ETierPromotionType.All,
  },
  {
    label: "Applicable to selected products",
    value: ETierPromotionType.Include,
  },
  {
    label: "Not applicable to selected products",
    value: ETierPromotionType.Exclude,
  },
]

export const PromotionProductOptions: Option[] = [
  {
    label: "All",
    value: EPromotionCompareType.All,
  },
  {
    label: "Applicable to selected product",
    value: EPromotionCompareType.Include,
  },
  {
    label: "Not applicable to selected product",
    value: EPromotionCompareType.Exclude,
  },
]

export const PromotionCustomerOptions: Option[] = [
  {
    label: "All",
    value: EPromotionCompareType.All,
  },
  {
    label: "Applicable to selected customers",
    value: EPromotionCompareType.Include,
  },
  {
    label: "Not applicable to selected customers",
    value: EPromotionCompareType.Exclude,
  },
]
export const TierPromotionCustomerGroupOptions: OptionItem[] = [
  {
    label: "All",
    value: ETierPromotionType.All,
  },
  {
    label: "Customer group who can access",
    value: ETierPromotionType.Include,
  },
  {
    label: "Customer group who cannot access",
    value: ETierPromotionType.Exclude,
  },
]

export const THUMBNAIL_FILE_POSTFIX = "-thumbnail-image"
